/**
 * Repository Error Hierarchy - Life-and-Death Grade Error Handling
 *
 * This comprehensive error system ensures no failure modes are missed
 * and provides detailed context for debugging and monitoring.
 */
import { z } from 'zod';
/**
 * Core error types matching backend error categories
 */
export var RepositoryErrorType;
(function (RepositoryErrorType) {
    // Data access errors
    RepositoryErrorType["NOT_FOUND"] = "NOT_FOUND";
    RepositoryErrorType["ALREADY_EXISTS"] = "ALREADY_EXISTS";
    RepositoryErrorType["INVALID_DATA"] = "INVALID_DATA";
    RepositoryErrorType["VALIDATION_FAILED"] = "VALIDATION_FAILED";
    // Infrastructure errors
    RepositoryErrorType["STORAGE_ERROR"] = "STORAGE_ERROR";
    RepositoryErrorType["CONNECTION_ERROR"] = "CONNECTION_ERROR";
    RepositoryErrorType["NETWORK_ERROR"] = "NETWORK_ERROR";
    RepositoryErrorType["TIMEOUT_ERROR"] = "TIMEOUT_ERROR";
    // Authorization errors
    RepositoryErrorType["UNAUTHORIZED"] = "UNAUTHORIZED";
    RepositoryErrorType["FORBIDDEN"] = "FORBIDDEN";
    RepositoryErrorType["TENANT_ISOLATION_VIOLATED"] = "TENANT_ISOLATION_VIOLATED";
    // Rate limiting and throttling
    RepositoryErrorType["RATE_LIMIT_EXCEEDED"] = "RATE_LIMIT_EXCEEDED";
    RepositoryErrorType["QUOTA_EXCEEDED"] = "QUOTA_EXCEEDED";
    RepositoryErrorType["THROTTLED"] = "THROTTLED";
    // System errors
    RepositoryErrorType["SERVICE_UNAVAILABLE"] = "SERVICE_UNAVAILABLE";
    RepositoryErrorType["CIRCUIT_BREAKER_OPEN"] = "CIRCUIT_BREAKER_OPEN";
    RepositoryErrorType["DEPENDENCY_FAILED"] = "DEPENDENCY_FAILED";
    // Data consistency errors
    RepositoryErrorType["STALE_DATA"] = "STALE_DATA";
    RepositoryErrorType["CONFLICT"] = "CONFLICT";
    RepositoryErrorType["PRECONDITION_FAILED"] = "PRECONDITION_FAILED";
    // Critical errors
    RepositoryErrorType["SAFETY_VIOLATION"] = "SAFETY_VIOLATION";
    RepositoryErrorType["WORKFLOW_CRITICAL_ERROR"] = "WORKFLOW_CRITICAL_ERROR";
    // Unknown/unexpected errors
    RepositoryErrorType["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
    RepositoryErrorType["SYSTEM_ERROR"] = "SYSTEM_ERROR";
})(RepositoryErrorType || (RepositoryErrorType = {}));
/**
 * Error severity levels for triage and alerting
 */
export var ErrorSeverity;
(function (ErrorSeverity) {
    ErrorSeverity["LOW"] = "LOW";
    ErrorSeverity["MEDIUM"] = "MEDIUM";
    ErrorSeverity["HIGH"] = "HIGH";
    ErrorSeverity["CRITICAL"] = "CRITICAL";
    ErrorSeverity["FATAL"] = "FATAL"; // System cannot continue, immediate shutdown needed
})(ErrorSeverity || (ErrorSeverity = {}));
/**
 * Error context schema for consistent error reporting
 */
const ErrorContextSchema = z.object({
    // Request context
    correlationId: z.string().optional(),
    requestId: z.string().optional(),
    sessionId: z.string().optional(),
    // Tenant and user context
    tenant: z.string().optional(),
    userId: z.string().optional(),
    actorId: z.string().optional(),
    // Operation context
    operation: z.string().optional(),
    resource: z.string().optional(),
    resourceId: z.string().optional(),
    // Technical context
    component: z.string().optional(),
    service: z.string().optional(),
    method: z.string().optional(),
    // Timing context
    timestamp: z.string().optional(),
    duration: z.number().optional(),
    retryCount: z.number().optional(),
    // Error chain context
    cause: z.unknown().optional(),
    stackTrace: z.string().optional(),
    // Additional metadata
    metadata: z.record(z.unknown()).optional()
});
/**
 * Base repository error class with comprehensive context
 */
export class RepositoryError extends Error {
    constructor(type, message, context = {}, severity = ErrorSeverity.MEDIUM, isRetryable = false, userMessage) {
        super(message);
        this.name = 'RepositoryError';
        this.type = type;
        this.severity = severity;
        this.context = this.validateContext(context);
        this.timestamp = new Date();
        this.isRetryable = isRetryable;
        this.userMessage = userMessage;
        // Ensure proper stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, RepositoryError);
        }
    }
    validateContext(context) {
        try {
            return ErrorContextSchema.parse(context);
        }
        catch (validationError) {
            // If context validation fails, use minimal context
            console.warn('Invalid error context provided:', validationError);
            return {
                metadata: { invalidContext: context, validationError }
            };
        }
    }
    /**
     * Create detailed error report for logging and monitoring
     */
    toReport() {
        return {
            type: this.type,
            severity: this.severity,
            message: this.message,
            userMessage: this.userMessage,
            context: this.context,
            timestamp: this.timestamp.toISOString(),
            isRetryable: this.isRetryable,
            stackTrace: this.stack,
            errorId: this.generateErrorId()
        };
    }
    /**
     * Generate unique error ID for tracking
     */
    generateErrorId() {
        return `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    }
    /**
     * Check if this error should trigger alerts
     */
    shouldAlert() {
        return this.severity === ErrorSeverity.CRITICAL || this.severity === ErrorSeverity.FATAL;
    }
    /**
     * Check if error indicates system health issues
     */
    indicatesSystemIssue() {
        const systemErrorTypes = [
            RepositoryErrorType.SERVICE_UNAVAILABLE,
            RepositoryErrorType.CIRCUIT_BREAKER_OPEN,
            RepositoryErrorType.DEPENDENCY_FAILED,
            RepositoryErrorType.SYSTEM_ERROR,
            RepositoryErrorType.WORKFLOW_CRITICAL_ERROR
        ];
        return systemErrorTypes.includes(this.type);
    }
    // Static factory methods for common error patterns
    static notFound(resource, id, context = {}) {
        return new RepositoryError(RepositoryErrorType.NOT_FOUND, `${resource} not found${id ? `: ${id}` : ''}`, { ...context, resource, resourceId: id }, ErrorSeverity.LOW, false, `The requested ${resource.toLowerCase()} could not be found.`);
    }
    static unauthorized(operation, context = {}) {
        return new RepositoryError(RepositoryErrorType.UNAUTHORIZED, `Unauthorized to perform ${operation}`, { ...context, operation }, ErrorSeverity.HIGH, false, 'You do not have permission to perform this action.');
    }
    static forbidden(resource, operation, context = {}) {
        return new RepositoryError(RepositoryErrorType.FORBIDDEN, `Forbidden: cannot ${operation} ${resource}`, { ...context, resource, operation }, ErrorSeverity.HIGH, false, 'Access to this resource is forbidden.');
    }
    static tenantViolation(expectedTenant, actualTenant, context = {}) {
        return new RepositoryError(RepositoryErrorType.TENANT_ISOLATION_VIOLATED, `Tenant isolation violation: expected ${expectedTenant}, got ${actualTenant}`, { ...context, metadata: { expectedTenant, actualTenant } }, ErrorSeverity.CRITICAL, false, 'Access denied due to tenant isolation policies.');
    }
    static validationFailed(details, context = {}) {
        const message = typeof details === 'string' ? details : 'Validation failed';
        return new RepositoryError(RepositoryErrorType.VALIDATION_FAILED, message, { ...context, metadata: { validationDetails: details } }, ErrorSeverity.MEDIUM, false, 'The provided data is invalid. Please check your input and try again.');
    }
    static connectionError(service, cause, context = {}) {
        return new RepositoryError(RepositoryErrorType.CONNECTION_ERROR, `Connection failed to ${service}: ${cause.message}`, { ...context, service, cause: cause.message }, ErrorSeverity.HIGH, true, 'Service temporarily unavailable. Please try again in a moment.');
    }
    static timeout(operation, duration, context = {}) {
        return new RepositoryError(RepositoryErrorType.TIMEOUT_ERROR, `Operation '${operation}' timed out after ${duration}ms`, { ...context, operation, duration }, ErrorSeverity.MEDIUM, true, 'The operation is taking longer than expected. Please try again.');
    }
    static rateLimited(limit, window, context = {}) {
        return new RepositoryError(RepositoryErrorType.RATE_LIMIT_EXCEEDED, `Rate limit exceeded: ${limit} requests per ${window}`, { ...context, metadata: { limit, window } }, ErrorSeverity.MEDIUM, true, 'Too many requests. Please wait a moment before trying again.');
    }
    static circuitOpen(service, context = {}) {
        return new RepositoryError(RepositoryErrorType.CIRCUIT_BREAKER_OPEN, `Circuit breaker open for service: ${service}`, { ...context, service }, ErrorSeverity.HIGH, true, 'Service is temporarily unavailable due to recent failures.');
    }
    static conflict(resource, reason, context = {}) {
        return new RepositoryError(RepositoryErrorType.CONFLICT, `Conflict updating ${resource}: ${reason}`, { ...context, resource, metadata: { conflictReason: reason } }, ErrorSeverity.MEDIUM, false, 'This resource has been modified by another user. Please refresh and try again.');
    }
    static safetyViolation(violation, context = {}) {
        return new RepositoryError(RepositoryErrorType.SAFETY_VIOLATION, `Safety violation detected: ${violation}`, context, ErrorSeverity.CRITICAL, false, 'This action violates safety constraints and cannot be performed.');
    }
    static workflowCritical(workflowId, error, context = {}) {
        return new RepositoryError(RepositoryErrorType.WORKFLOW_CRITICAL_ERROR, `Critical error in workflow ${workflowId}: ${error}`, { ...context, resourceId: workflowId }, ErrorSeverity.CRITICAL, false, 'A critical workflow error has occurred. Please contact support immediately.');
    }
    static fromUnknown(error, context = {}) {
        if (error instanceof RepositoryError) {
            return error;
        }
        if (error instanceof Error) {
            return new RepositoryError(RepositoryErrorType.SYSTEM_ERROR, error.message, { ...context, cause: error.message, stackTrace: error.stack }, ErrorSeverity.HIGH, false, 'An unexpected error occurred. Please try again or contact support.');
        }
        return new RepositoryError(RepositoryErrorType.UNKNOWN_ERROR, `Unknown error: ${String(error)}`, { ...context, cause: error }, ErrorSeverity.HIGH, false, 'An unexpected error occurred. Please try again or contact support.');
    }
}
/**
 * Error aggregation for collecting multiple errors
 */
export class ErrorCollection extends Error {
    constructor(errors, message = `Multiple errors occurred (${errors.length} total)`) {
        super(message);
        this.errors = errors;
        this.name = 'ErrorCollection';
    }
    /**
     * Get the most severe error in the collection
     */
    getMostSevere() {
        return this.errors.reduce((most, current) => {
            const severityOrder = {
                [ErrorSeverity.LOW]: 1,
                [ErrorSeverity.MEDIUM]: 2,
                [ErrorSeverity.HIGH]: 3,
                [ErrorSeverity.CRITICAL]: 4,
                [ErrorSeverity.FATAL]: 5
            };
            return severityOrder[current.severity] > severityOrder[most.severity] ? current : most;
        });
    }
    /**
     * Check if any errors require alerting
     */
    hasAlertableErrors() {
        return this.errors.some(error => error.shouldAlert());
    }
    /**
     * Get all errors of a specific type
     */
    getByType(type) {
        return this.errors.filter(error => error.type === type);
    }
    /**
     * Convert to structured report
     */
    toReport() {
        return {
            summary: this.message,
            errors: this.errors.map(error => error.toReport())
        };
    }
}
/**
 * Type guards for error handling
 */
export const isRepositoryError = (error) => {
    return error instanceof RepositoryError;
};
export const isErrorCollection = (error) => {
    return error instanceof ErrorCollection;
};
/**
 * Error type predicates for specific error handling
 */
export const isRetryableError = (error) => {
    return error.isRetryable;
};
export const isAuthError = (error) => {
    return [
        RepositoryErrorType.UNAUTHORIZED,
        RepositoryErrorType.FORBIDDEN,
        RepositoryErrorType.TENANT_ISOLATION_VIOLATED
    ].includes(error.type);
};
export const isSystemError = (error) => {
    return error.indicatesSystemIssue();
};
export const isCriticalError = (error) => {
    return error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.FATAL;
};
//# sourceMappingURL=RepositoryError.js.map